import * as THREE from 'three'
import type {
  MemeGeometryRecoveryPayload,
  MemeMetaFile,
  RecoveredViewerBounds,
  ViewpointBounds,
  ViewpointCoordinateConvention,
  ViewpointSceneContext,
  ViewpointSceneElement,
  ViewpointVector3,
} from '../generate-jig.types'
import {
  applyConventionToCenter,
  buildMeshNameVariants,
  detectCoordinateConvention,
  type ConventionPartLike,
  type CoordinateConventionDetectionResult,
  type CoordinateConventionMode,
} from './coordinate-convention'
import type {
  ViewpointVisibilityContext,
  ViewpointVisibilityElement,
  ViewpointVisibilitySample,
} from './visibility-context'

export interface ViewpointConventionClassification {
  mode: CoordinateConventionMode | 'unknown'
  detection: CoordinateConventionDetectionResult
  fallbackReason?: string
}

function normalizeVector(vector: ViewpointVector3): ViewpointVector3 {
  const magnitude = Math.sqrt((vector.x * vector.x) + (vector.y * vector.y) + (vector.z * vector.z))
  if (magnitude < 1e-10) {
    return { x: 0, y: 0, z: 1 }
  }

  return {
    x: vector.x / magnitude,
    y: vector.y / magnitude,
    z: vector.z / magnitude,
  }
}

function buildUnknownFallbackReason(detection: CoordinateConventionDetectionResult): string {
  if (detection.resolution === 'missing-reference') {
    return 'missing memeMeta correlation data'
  }
  if (detection.resolution === 'degenerate-reference') {
    return 'degenerate memeMeta reference data'
  }
  if (detection.matchedPartCount === 0) {
    return 'no matched parts'
  }
  if (detection.xCorrelation === null || detection.zCorrelation === null) {
    return 'insufficient correlation evidence'
  }
  return 'ambiguous x/z correlations'
}

function buildConventionMetadata(classification: ViewpointConventionClassification): ViewpointCoordinateConvention {
  return {
    mode: classification.mode,
    resolution: classification.detection.resolution,
    source: classification.detection.source,
    detectionSource: classification.detection.detectionSource,
    matchedPartCount: classification.detection.matchedPartCount,
    xCorrelation: classification.detection.xCorrelation,
    zCorrelation: classification.detection.zCorrelation,
    matchedParts: classification.detection.matchedParts,
    fallbackReason: classification.fallbackReason,
  }
}

function getElementDisplayName(element: ViewpointSceneElement): string {
  const displayAlias = element.aliases.find(function (alias) {
    return alias && alias !== element.id
  })
  return displayAlias || element.id
}

function toConventionParts(context: ViewpointSceneContext): ConventionPartLike[] {
  return context.elements.map(function (element) {
    return {
      name: getElementDisplayName(element),
      objectName: element.id,
      center: element.bounds.center,
      size: element.bounds.size,
    }
  })
}

function recomputeSceneBounds(elements: ViewpointSceneElement[]): ViewpointBounds | null {
  if (!elements.length) {
    return null
  }

  const min = { x: Infinity, y: Infinity, z: Infinity }
  const max = { x: -Infinity, y: -Infinity, z: -Infinity }

  elements.forEach(function (element) {
    min.x = Math.min(min.x, element.bounds.center.x - (element.bounds.size.x / 2))
    min.y = Math.min(min.y, element.bounds.center.y - (element.bounds.size.y / 2))
    min.z = Math.min(min.z, element.bounds.center.z - (element.bounds.size.z / 2))
    max.x = Math.max(max.x, element.bounds.center.x + (element.bounds.size.x / 2))
    max.y = Math.max(max.y, element.bounds.center.y + (element.bounds.size.y / 2))
    max.z = Math.max(max.z, element.bounds.center.z + (element.bounds.size.z / 2))
  })

  return {
    center: {
      x: (min.x + max.x) / 2,
      y: (min.y + max.y) / 2,
      z: (min.z + max.z) / 2,
    },
    size: {
      x: max.x - min.x,
      y: max.y - min.y,
      z: max.z - min.z,
    },
  }
}

function buildElementBoundsMap(elements: ViewpointSceneElement[]): Map<string, ViewpointBounds> {
  const elementBounds = new Map<string, ViewpointBounds>()

  elements.forEach(function (element) {
    element.aliases.forEach(function (alias) {
      elementBounds.set(alias, element.bounds)
    })
  })

  return elementBounds
}

function buildSafeAggregateFallbackElements(
  baseSceneContext: ViewpointSceneContext,
  modelOrigin?: ViewpointVector3 | null
): ViewpointSceneElement[] {
  const aggregateBounds: ViewpointBounds = {
    center: {
      x: modelOrigin ? modelOrigin.x : baseSceneContext.sceneBounds.center.x,
      y: baseSceneContext.sceneBounds.center.y,
      z: modelOrigin ? modelOrigin.z : baseSceneContext.sceneBounds.center.z,
    },
    size: {
      x: baseSceneContext.sceneBounds.size.x,
      y: baseSceneContext.sceneBounds.size.y,
      z: baseSceneContext.sceneBounds.size.z,
    },
  }

  return baseSceneContext.elements.map(function (element) {
    return {
      id: element.id,
      aliases: element.aliases.slice(),
      bounds: aggregateBounds,
    }
  })
}

function buildTransformMatrix(mode: CoordinateConventionMode): THREE.Matrix4 {
  if (mode === 'zFlip') {
    return new THREE.Matrix4().makeScale(1, 1, -1)
  }

  if (mode === 'y180') {
    return new THREE.Matrix4().makeScale(-1, 1, -1)
  }

  return new THREE.Matrix4().identity()
}

function transformBounds(bounds: ViewpointBounds, mode: CoordinateConventionMode): ViewpointBounds {
  return {
    center: applyConventionToCenter(bounds.center, mode) || bounds.center,
    size: {
      x: bounds.size.x,
      y: bounds.size.y,
      z: bounds.size.z,
    },
  }
}

function transformVisibilityContext(
  baseContext: ViewpointVisibilityContext | null | undefined,
  mode: CoordinateConventionMode
): ViewpointVisibilityContext | null {
  if (!baseContext) {
    return null
  }

  const transformMatrix = buildTransformMatrix(mode)
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(transformMatrix)
  const elements: ViewpointVisibilityElement[] = baseContext.elements.map(function (element) {
    const mesh = element.mesh.clone()
    mesh.matrixAutoUpdate = false
    mesh.matrixWorld.copy(transformMatrix.clone().multiply(element.mesh.matrixWorld.clone()))
    mesh.matrix.copy(mesh.matrixWorld)
    mesh.updateMatrixWorld(true)

    const samples = element.samples.map(function (sample: ViewpointVisibilitySample) {
      const point = new THREE.Vector3(sample.point.x, sample.point.y, sample.point.z).applyMatrix4(transformMatrix)
      const normal = new THREE.Vector3(sample.normal.x, sample.normal.y, sample.normal.z).applyMatrix3(normalMatrix)
      const normalizedNormal = normalizeVector({ x: normal.x, y: normal.y, z: normal.z })

      return {
        point: { x: point.x, y: point.y, z: point.z },
        normal: normalizedNormal,
        weight: sample.weight,
      }
    })

    return {
      id: element.id,
      aliases: element.aliases.slice(),
      bounds: transformBounds(element.bounds, mode),
      mesh: mesh,
      samples: samples,
    }
  })

  const elementMap = new Map<string, ViewpointVisibilityElement>()
  const aliasMap = new Map<string, ViewpointVisibilityElement[]>()

  elements.forEach(function (element) {
    elementMap.set(element.id, element)
    aliasMap.set(element.id, [element])
    element.aliases.forEach(function (alias) {
      const matches = aliasMap.get(alias) || []
      matches.push(element)
      aliasMap.set(alias, matches)
    })
  })

  return {
    elements: elements,
    elementMap: elementMap,
    aliasMap: aliasMap,
  }
}

export function classifyViewpointConvention(
  context: ViewpointSceneContext,
  memeMetaData?: MemeMetaFile | null
): ViewpointConventionClassification {
  const detection = detectCoordinateConvention(toConventionParts(context), memeMetaData)

  if (detection.mode) {
    return {
      mode: detection.mode,
      detection: detection,
    }
  }

  return {
    mode: 'unknown',
    detection: detection,
    fallbackReason: buildUnknownFallbackReason(detection),
  }
}

function buildRecoveredLookup(recoveredBounds: MemeGeometryRecoveryPayload | null): Map<string, RecoveredViewerBounds> | null {
  if (!recoveredBounds || !recoveredBounds.subObjects || recoveredBounds.subObjects.length === 0) {
    return null
  }

  const lookup = new Map<string, RecoveredViewerBounds>()
  recoveredBounds.subObjects.forEach(function (subObject) {
    buildMeshNameVariants(subObject.name).forEach(function (variant) {
      if (!lookup.has(variant)) {
        lookup.set(variant, subObject)
      }
    })
  })
  return lookup
}

function findRecoveredMatch(
  element: ViewpointSceneElement,
  lookup: Map<string, RecoveredViewerBounds>
): RecoveredViewerBounds | null {
  const displayName = getElementDisplayName(element)
  const variantSet = new Set<string>()
  buildMeshNameVariants(displayName).forEach(function (variant) { variantSet.add(variant) })
  buildMeshNameVariants(element.id).forEach(function (variant) { variantSet.add(variant) })
  element.aliases.forEach(function (alias) {
    buildMeshNameVariants(alias).forEach(function (variant) { variantSet.add(variant) })
  })

  for (const variant of variantSet) {
    const match = lookup.get(variant)
    if (match) {
      return match
    }
  }

  return null
}

function buildRecoveredElementMap(
  elements: ViewpointSceneElement[],
  recoveredBounds: MemeGeometryRecoveryPayload | null
): Map<string, RecoveredViewerBounds> | null {
  const lookup = buildRecoveredLookup(recoveredBounds)
  if (!lookup) {
    return null
  }

  const recoveredById = new Map<string, RecoveredViewerBounds>()
  for (const element of elements) {
    const recovered = findRecoveredMatch(element, lookup)
    if (!recovered) {
      return null
    }
    recoveredById.set(element.id, recovered)
  }

  return recoveredById
}

export function normalizeViewpointSceneContext(options: {
  baseSceneContext: ViewpointSceneContext
  modelOrigin?: ViewpointVector3 | null
  memeMetaData?: MemeMetaFile | null
  recoveredBounds?: MemeGeometryRecoveryPayload | null
}): ViewpointSceneContext {
  const recoveredById = buildRecoveredElementMap(options.baseSceneContext.elements, options.recoveredBounds || null)
  const hasRecovery = recoveredById !== null

  const classification = classifyViewpointConvention(options.baseSceneContext, options.memeMetaData)
  const coordinateConvention = hasRecovery
    ? Object.assign({}, buildConventionMetadata(classification), {
      source: 'meme-geometry' as const,
      resolution: 'resolved' as const,
    })
    : buildConventionMetadata(classification)

  // eslint-disable-next-line no-console
  console.log(
    '[normalizer]'
    + ' recoveryProvided=' + !!options.recoveredBounds
    + ' recoveryApplied=' + hasRecovery
    + ' mode=' + classification.mode
    + ' resolution=' + classification.detection.resolution
    + ' elements=' + options.baseSceneContext.elements.length
  )

  if (!hasRecovery && (classification.mode === 'unknown' || classification.mode === 'identity')) {
    if (classification.detection.resolution === 'degenerate-reference') {
      const safeFallbackElements = buildSafeAggregateFallbackElements(options.baseSceneContext, options.modelOrigin || null)
      // eslint-disable-next-line no-console
      console.log('[normalizer] fail-closed aggregate fallback for degenerate-reference')
      return {
        sceneBounds: recomputeSceneBounds(safeFallbackElements) || options.baseSceneContext.sceneBounds,
        elements: safeFallbackElements,
        elementBounds: buildElementBoundsMap(safeFallbackElements),
        visibilityContext: null,
        coordinateConvention: coordinateConvention,
      }
    }

    // eslint-disable-next-line no-console
    console.log('[normalizer] pass-through — using raw setFromObject bounds (no recovery, no convention to apply)')
    return Object.assign({}, options.baseSceneContext, {
      coordinateConvention: coordinateConvention,
    })
  }

  const resolvedMode: CoordinateConventionMode | null = classification.mode === 'unknown' ? null : classification.mode
  const elements = options.baseSceneContext.elements.map(function (element) {
    const recovered = recoveredById ? (recoveredById.get(element.id) || null) : null
    const nextBounds: ViewpointBounds = recovered
      ? { center: recovered.center, size: recovered.size }
      : (resolvedMode ? transformBounds(element.bounds, resolvedMode) : element.bounds)
    return {
      id: element.id,
      aliases: element.aliases.slice(),
      bounds: nextBounds,
    }
  })

  const sampleElements = elements.slice(0, 3).map(function (element) {
    const c = element.bounds.center
    return element.id.split('_').slice(-1)[0] + '=(' + c.x.toFixed(3) + ',' + c.y.toFixed(3) + ',' + c.z.toFixed(3) + ')'
  }).join(' ')
  // eslint-disable-next-line no-console
  console.log(
    '[normalizer] applied path='
    + (hasRecovery ? 'recovery' : (resolvedMode ? 'convention-transform:' + resolvedMode : 'pass-through'))
    + ' sample bounds: ' + sampleElements
  )
  const elementBounds = buildElementBoundsMap(elements)

  const visibilityMode: CoordinateConventionMode | null = hasRecovery ? null : resolvedMode
  return {
    sceneBounds: recomputeSceneBounds(elements) || options.baseSceneContext.sceneBounds,
    elements: elements,
    elementBounds: elementBounds,
    visibilityContext: hasRecovery
      ? null
      : (visibilityMode
        ? transformVisibilityContext(options.baseSceneContext.visibilityContext || null, visibilityMode)
        : (options.baseSceneContext.visibilityContext || null)),
    coordinateConvention: coordinateConvention,
  }
}
